import { BOX_SHADOW_16 } from '@apps/card/constants/boxShadow';
import { templateToTitle } from '@apps/card/utils/templateToTitle';
import { useCardBackTemplatesByCategoryAndDimensionsQuery } from './CardBackChoiceGroup.graphql.generated';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import type { ReactNode } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import type { PageDimensions, PageJson, ThemeJson } from '../CardCustomizer.types';
import { useCustomizationContext } from '../useCardCustomization';
import { ChoiceGroup } from './ChoiceGroup';
import { SkeletonThumbnail } from '@shared/components/Skeleton';
import type { LayerData } from './Layer.types';
import { useAutofill } from '@apps/card/contexts/AutofillContext';
import { cloneDeep } from 'lodash-es';
import { Page } from './Page';
import { useScaleToContainer } from '@apps/card/hooks/useScaleToContainer';
import { useTranslation } from '@shared/core';
import { isPostcardDimensions, orderIncludedFirst, selectBackTemplates } from './CardBackChoiceGroup.utils';
import { isRecolorableBackLayout, previewWithFrontColors } from '@apps/card/utils/frontColorsOnBack';
import { findInkGlyphSrcs } from '@apps/card/utils/inkGlyph';

export const PLACEHOLDER_TEMPLATE = {
  fill: 'rgb(255,255,255)',
  layout: 'placeholder', // aka themeId
  layers: [] as LayerData[],
};

const TEMPLATE_ORDER = [
  'lets_celebrate',
  'lets_celebrate_qr',
  'photo_text',
  'photo_inset',
  'the_details',
  'accommodations_classic',
  'kindly_rsvp',
  'accommodations_handwritten',
  'photo_qr_code',
  'photo_monogram',
  'monogram',
  'photo_full',
  'typography',

  'adorable_thank_you',
  'many_thanks_thank_you',
  'monogram_thank_you',
  'names_thank_you',
  'photo_blank_thank_you',
  'photo_note_thank_you',

  'photo_note',
  'photo_note_red',
  'photo_note_blue',
  'photo_note_beige',
  'photo_note_gold',
  'photo_note_full',
  'photo_note_full_red',
  'photo_note_full_blue',
  'photo_note_full_green',
  'large_photo',
  'large_photo_red',
  'large_photo_blue',
  'large_photo_gold',
  'large_photo_green',
  'large_photo_tan',
  'duo',
  'duo_blue',
  'duo_red',
  'duo_green',
  'photo_booth',
  'photo_booth_snow',
  'collage',
  'photo_album',
  'all_photos',
  'gallery',
  'simple_message_red',
  'simple_message_blue',
  'simple_message_green',
  'simple_message',
  'plaid_red',
  'plaid_green',
  'plaid_blue',
  'snow_red',
  'snow_blue',
  'dots_red',
  'snow_gold',
  'dots_blue',
  'dots_green',
  'dots',
  'dots_cream',
  'blank_red',
  'blank_green',
  'blank_blue',

  'blank',
];

// Holiday card backs (templates where side=back and category=holiday).
// Kept separate so seasonal variants don't clutter the main list.
const TEMPLATE_ORDER_HOLIDAY = [
  'photo_note',
  'photo_note_red',
  'photo_note_blue',
  'photo_note_beige',
  'photo_note_gold',
  'photo_note_full',
  'photo_note_full_red',
  'photo_note_full_blue',
  'photo_note_full_green',
  'large_photo',
  'large_photo_red',
  'large_photo_blue',
  'large_photo_gold',
  'large_photo_green',
  'large_photo_tan',
  'duo',
  'duo_blue',
  'duo_red',
  'duo_green',
  'photo_booth',
  'photo_booth_snow',
  'collage',
  'photo_album',
  'all_photos',
  'gallery',
  'simple_message_red',
  'simple_message_blue',
  'simple_message_green',
  'simple_message',
  'plaid_red',
  'plaid_green',
  'plaid_blue',
  'snow_red',
  'snow_blue',
  'dots_red',
  'snow_gold',
  'dots_blue',
  'dots_green',
  'dots',
  'dots_cream',
  'blank_red',
  'blank_green',
  'blank_blue',

  'blank',
];

const TEMPLATE_ORDER_ENCLOSURE = [
  'lets_celebrate',
  'lets_celebrate_qr',
  'photo_text',
  'photo_inset',
  'accommodations_classic',
  'the_details',
  'kindly_rsvp',
  'accommodations_handwritten',
  'photo_qr_code',
  'photo_monogram',
  'monogram',
  'photo_full',
  'typography',

  'blank',
];

// 'blank' always sorts last, ahead of it any template not listed here.
const sortRank = (order: string[], themeId: string) => {
  if (themeId === 'blank') return Number.MAX_SAFE_INTEGER;
  const index = order.indexOf(themeId);
  return index === -1 ? Number.MAX_SAFE_INTEGER - 1 : index;
};

export interface StationeryTemplate {
  id: string;
  name?: string | null;
  themeId: string;
  themeJSON: ThemeJson;
  thumbnailUrl?: string | null;
}

// Some temporarily asymmetry with `themeId` in and `setTemplate` out.
// Ultimately we want to have `templateId` inbound.
// However this is blocked on us migrating existing drafts that are still stuck on `themeId`.
type CardBackChoiceGroupProps = Readonly<{
  themeId: string;
  setTemplate: (template: StationeryTemplate) => void;
  dimensions: PageDimensions;
  price?: string;
  /** Rendered between the `Layout` title row and the thumbnails — see `ChoiceGroup`'s `afterTitle`. */
  afterTitle?: ReactNode;
  /**
   * Whether a layout belongs to a product the draft is not on yet. Such a layout stays selectable —
   * choosing it is how the user upgrades — but it is marked, it sorts after the included ones, and
   * `onPremiumOptionSelect` handles it instead of `setTemplate`.
   *
   * Passed in rather than derived here so this component stays product-agnostic, matching how
   * `SilhouetteChoiceGroup` and `DigitalCardColorPicker` take the same trio. All three are
   * optional, so the gating is opt-in.
   *
   * Keyed by `themeId` rather than the template, because `themeId` is what the draft stores and
   * what the policy that answers this speaks.
   *
   * **Must be referentially stable**: it feeds the memo that orders the grid.
   */
  isOptionPremium?: (themeId: string) => boolean;
  onPremiumOptionSelect?: (template: StationeryTemplate) => void;
  /** Rendered in the tile's top-right corner, over the artwork -- give it a backdrop to stay legible. */
  renderPremiumBadge?: () => ReactNode;
}>;

export const CardBackChoiceGroup = (props: CardBackChoiceGroupProps) => {
  const { tq } = useTranslation('stationery');
  const { themeId, setTemplate, dimensions, price, afterTitle, isOptionPremium, onPremiumOptionSelect, renderPremiumBadge } = props;

  const [category] = useCustomizationContext(draft => draft.stationeryTemplateCategory);
  // Thumbnails preview the *result* of picking a layout, so while "Match front colors" is on
  // they wear the front's colors too. Read from the draft rather than threaded through props, so
  // ticking the checkbox restyles the grid immediately.
  const [cardFront] = useCustomizationContext(draft => draft.config.card.front);
  const [useFrontColors] = useCustomizationContext(draft => !!draft.config.card.back?.useFrontColors);

  let normalizedCategory = category;
  // 'invitation' is also serving as the card back category for 'saveTheDate' cards.
  if (category === StationeryTemplateCategoryEnum.saveTheDate) normalizedCategory = StationeryTemplateCategoryEnum.invitation;

  const { data } = useCardBackTemplatesByCategoryAndDimensionsQuery({
    variables: {
      filter: {
        enabled: true,
        side: 'back',
        categories: [normalizedCategory],
        widthInches: dimensions.width,
        heightInches: dimensions.height,
      },
    },
    batchMode: 'fast',
  });

  // For postcard sizes, also fetch the rotated orientation to use as a fallback
  // (see CardBackChoiceGroup.utils.ts). Other sizes keep exact matching.
  const isPostcardSize = isPostcardDimensions(dimensions);
  const { data: rotatedData } = useCardBackTemplatesByCategoryAndDimensionsQuery({
    variables: {
      filter: {
        enabled: true,
        side: 'back',
        categories: [normalizedCategory],
        widthInches: dimensions.height,
        heightInches: dimensions.width,
      },
    },
    batchMode: 'fast',
    skip: !isPostcardSize,
  });

  const { autofill } = useAutofill();

  const availableTemplates = useMemo(() => {
    const sortedCopy = [
      ...selectBackTemplates({
        exactOrientation: data?.stationeryTemplatesContainer.pagedResults.edges.map(edge => edge.node) ?? [],
        rotatedOrientation: rotatedData?.stationeryTemplatesContainer.pagedResults.edges.map(edge => edge.node) ?? [],
        exactQueryCompleted: data !== undefined,
        isPostcardSize,
      }),
    ];
    const orderByCategory: Partial<Record<StationeryTemplateCategoryEnum, string[]>> = {
      [StationeryTemplateCategoryEnum.enclosure]: TEMPLATE_ORDER_ENCLOSURE,
      [StationeryTemplateCategoryEnum.holiday]: TEMPLATE_ORDER_HOLIDAY,
    };
    const order = orderByCategory[normalizedCategory] ?? TEMPLATE_ORDER;
    sortedCopy.sort((a, b) => sortRank(order, a.themeId) - sortRank(order, b.themeId));

    const indexOfUploadYourDesign = sortedCopy.findIndex(template =>
      [
        'upload_your_design',
        'upload_your_design_7x5',
        'upload_your_own', // deprecated, use `upload_your_design` instead
        'upload_your_own_design', // deprecated, use `upload_your_design` instead
      ].includes(template.themeId),
    );
    if (indexOfUploadYourDesign >= 0) {
      // Insert at index 5, but never after `blank`: splice clamps to the array end
      // on short result sets, which would park the upload layout past the blank-last rule.
      const [uploadTemplate] = sortedCopy.splice(indexOfUploadYourDesign, 1);
      const blankIndex = sortedCopy.findIndex(template => template.themeId === 'blank');
      const targetIndex = blankIndex === -1 ? 5 : Math.min(5, blankIndex);
      sortedCopy.splice(targetIndex, 0, uploadTemplate);
    }

    const clonedSortedCopy = sortedCopy.map(cloneDeep);
    clonedSortedCopy.forEach(template => {
      autofill(template);
    });

    return clonedSortedCopy;
  }, [data, rotatedData, isPostcardSize, normalizedCategory, autofill]);

  // A separate pass, deliberately outside the memo above: that one clones and autofills every
  // template and is keyed on the query results, and folding a per-product predicate into it would
  // redo all of that work whenever the product changed.
  const templates = useMemo(() => orderIncludedFirst(availableTemplates, template => !!isOptionPremium?.(template.themeId)), [availableTemplates, isOptionPremium]);

  // New drafts are created with a placeholder template.
  // If we see the placeholder, we replace it with the default here.
  // Should only happen once per draft.
  //
  // Always through the plain `setTemplate`, never the upsell: the ordering above puts an included
  // layout first, so on a gated product this picks a free one. Where a size has no included layout
  // at all it lands on a premium one without charging for it, which is the same "gating is on
  // interaction only" rule the rest of the policy follows.
  useEffect(() => {
    if (themeId !== PLACEHOLDER_TEMPLATE.layout) return;
    const defaultTemplate = templates[0];
    if (!defaultTemplate) return;
    setTemplate(defaultTemplate);
  }, [setTemplate, templates, themeId]);

  if (!templates.length)
    return (
      <ChoiceGroup
        title={tq`Layout`}
        value={{ themeId }}
        price={price}
        getName={templateToTitle}
        afterTitle={afterTitle}
      >
        <Box
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          columnGap={3}
          rowGap={5}
          width="100%"
        >
          <SkeletonThumbnail
            height={240}
            width="auto"
          />
          <SkeletonThumbnail
            height={240}
            width="auto"
          />
          <SkeletonThumbnail
            height={240}
            width="auto"
          />
          <SkeletonThumbnail
            height={240}
            width="auto"
          />
        </Box>
      </ChoiceGroup>
    );

  const selectedTemplate = templates.find(t => t.themeId === themeId) ?? templates[0];

  return (
    <>
      <ChoiceGroup
        title={tq`Layout`}
        value={selectedTemplate}
        choices={templates}
        price={price}
        setValue={template => {
          if (isOptionPremium?.(template.themeId)) {
            // The upsell owns the whole interaction, including writing the layout -- it has to
            // apply and persist the change as one step with the product switch.
            onPremiumOptionSelect?.(template);
            return;
          }
          setTemplate(template);
        }}
        getName={templateToTitle}
        afterTitle={afterTitle}
        renderChoiceBadge={template => (isOptionPremium?.(template.themeId) ? renderPremiumBadge?.() : undefined)}
      >
        {templates.map(template => (
          <CardBackThumbnail
            key={template.id}
            template={template}
            front={useFrontColors ? cardFront : undefined}
          />
        ))}
      </ChoiceGroup>
    </>
  );
};

const CardBackThumbnail = (props: { template: StationeryTemplate; front?: PageJson }) => {
  const { template, front } = props;

  const [containerRef, decoratorRef, contentRef] = useScaleToContainer<HTMLDivElement>(0, 'width');

  // Whether a template image is an ink glyph can only be told from its pixels, so the thumbnail
  // measures its own. Verdicts are cached per src and the editor canvas has usually measured the
  // same asset already, making this a synchronous cache hit in practice. Until it resolves the
  // preview simply renders without image fills, which is what it did before.
  const [inkGlyphSrcs, setInkGlyphSrcs] = useState<ReadonlySet<string>>();
  useEffect(() => {
    if (!front) return;
    let cancelled = false;
    void findInkGlyphSrcs(template.themeJSON.card.front.layers).then(srcs => {
      if (!cancelled) setInkGlyphSrcs(srcs);
    });
    return () => {
      cancelled = true;
    };
  }, [front, template]);

  // Recolored for display only. `template.themeJSON` stays pristine because `setTemplate` copies it
  // into the draft, and a pre-recolored copy would make the draft back up the front's colors as if
  // they were the layout's own.
  const page = useMemo(() => {
    const own = template.themeJSON.card.front;
    return front && isRecolorableBackLayout(template.themeId) ? previewWithFrontColors(own, front, inkGlyphSrcs) : own;
  }, [template, front, inkGlyphSrcs]);

  return (
    // The parent button is alignItems=center (which is a good default)
    // But here we set Flex height=100% so that in cases with mixed-number-of-lines buttons
    // so that everything aligns top.
    <Flex
      ref={containerRef}
      flexDirection="column"
      alignItems="center"
      rowGap={1}
      height="100%"
      width="100%"
    >
      <Box
        ref={decoratorRef}
        boxShadow={BOX_SHADOW_16}
        width="100%"
      >
        <Page
          ref={contentRef}
          // Event in side=back templates, the `card.front` is the actual content.
          page={page}
          width={5}
          height={7}
          rendition="small"
        />
      </Box>
      <Flex
        alignItems="center"
        justifyContent="center"
        columnGap={2}
        padding={2}
      >
        <TextV2
          typographyVariant="label2"
          color="mono12"
        >
          {templateToTitle(template)}
        </TextV2>
      </Flex>
    </Flex>
  );
};
